import { useEffect, useMemo, useRef, useState } from 'react';
import type { Locale } from '../../app/locale/resolveLocale';
import type { CoreEvent } from '../../data/choiceMaps';

export type GestureTelemetry = {
  distance: number;
  dwellTimeMs: number;
  velocity: number;
  reversalCount: number;
};

type PointerSnapshot = {
  pointerId: number;
  startX: number;
  startY: number;
  lastDirection?: SwipeDirection;
  reversalCount: number;
  startTime: number;
};

type SwipeDirection = 'up' | 'down' | 'left' | 'right';
type MotionPermissionResult = 'granted' | 'denied' | 'default';
type DeviceMotionConstructorWithPermission = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<MotionPermissionResult>;
};

const fallbackEvents: CoreEvent[] = [
  'swipe_up_escape',
  'swipe_down_confront',
  'swipe_left_play_cool',
  'swipe_right_fix',
  'double_tap_meme',
  'fast_fling_nope'
];

const directionEvents: Record<SwipeDirection, CoreEvent> = {
  up: 'swipe_up_escape',
  down: 'swipe_down_confront',
  left: 'swipe_left_play_cool',
  right: 'swipe_right_fix'
};

const hintCopy: Record<Locale, Record<CoreEvent, { gesture: string; label: string }>> = {
  'zh-CN': {
    swipe_up_escape: { gesture: '上滑', label: '先撤退' },
    swipe_down_confront: { gesture: '下滑', label: '当场说' },
    swipe_left_play_cool: { gesture: '左滑', label: '装没事' },
    swipe_right_fix: { gesture: '右滑', label: '处理一下' },
    long_press_suppress: { gesture: '长按', label: '憋一会' },
    double_tap_meme: { gesture: '双击', label: '玩个梗' },
    shake_spiral: { gesture: '摇一摇', label: '破防了' },
    fast_fling_nope: { gesture: '快滑', label: '直接走' },
    timed_hesitation: { gesture: '停住', label: '停20秒' }
  },
  en: {
    swipe_up_escape: { gesture: 'Swipe up', label: 'bow out' },
    swipe_down_confront: { gesture: 'Swipe down', label: 'speak up' },
    swipe_left_play_cool: { gesture: 'Swipe left', label: 'play it cool' },
    swipe_right_fix: { gesture: 'Swipe right', label: 'make it right' },
    long_press_suppress: { gesture: 'Long press', label: 'bite your tongue' },
    double_tap_meme: { gesture: 'Double tap', label: 'crack a joke' },
    shake_spiral: { gesture: 'Shake', label: 'losing it' },
    fast_fling_nope: { gesture: 'Fast swipe', label: 'hard pass' },
    timed_hesitation: { gesture: 'Pause', label: 'wait 20s' }
  }
};

const hesitationDelayMs = 20_000;
const feedbackVisibleMs = 1_400;

const stageCopy: Record<Locale, { label: string; instruction: string; dial: string; feedbackPrefix: string }> = {
  'zh-CN': {
    label: '手势舞台',
    instruction: '先按住中间，再滑向周边提示；下方动作可直接操作。',
    dial: '从中间滑向提示方向',
    feedbackPrefix: '已识别'
  },
  en: {
    label: 'Gesture stage',
    instruction: 'Start in the center, then move toward a hint. Use quick actions below.',
    dial: 'Start here, move toward a hint',
    feedbackPrefix: 'Detected'
  }
};

const directionalCoreEvents: CoreEvent[] = [
  'swipe_up_escape',
  'swipe_down_confront',
  'swipe_left_play_cool',
  'swipe_right_fix'
];

const extraCoreEvents: CoreEvent[] = [
  'long_press_suppress',
  'double_tap_meme',
  'shake_spiral',
  'fast_fling_nope',
  'timed_hesitation'
];

function distanceBetween(startX: number, startY: number, endX: number, endY: number) {
  return Math.hypot(endX - startX, endY - startY);
}

function coordinateFrom(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function pointFromPointerEvent(event: React.PointerEvent<HTMLDivElement>) {
  const nativeEvent = event.nativeEvent as PointerEvent & { pageX?: number; pageY?: number };
  return {
    x: coordinateFrom(event.clientX, coordinateFrom(nativeEvent.clientX, coordinateFrom(nativeEvent.pageX, 0))),
    y: coordinateFrom(event.clientY, coordinateFrom(nativeEvent.clientY, coordinateFrom(nativeEvent.pageY, 0)))
  };
}

function pointFromMouseEvent(event: React.MouseEvent<HTMLDivElement>) {
  const nativeEvent = event.nativeEvent as MouseEvent & { pageX?: number; pageY?: number };
  return {
    x: coordinateFrom(event.clientX, coordinateFrom(nativeEvent.clientX, coordinateFrom(nativeEvent.pageX, 0))),
    y: coordinateFrom(event.clientY, coordinateFrom(nativeEvent.clientY, coordinateFrom(nativeEvent.pageY, 0)))
  };
}

function dominantDirection(dx: number, dy: number): SwipeDirection | undefined {
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return undefined;
  if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) return undefined;
  if (Math.abs(dy) > Math.abs(dx)) return dy < 0 ? 'up' : 'down';
  return dx < 0 ? 'left' : 'right';
}

function hintPosition(event: CoreEvent) {
  if (event === 'swipe_up_escape') return 'top';
  if (event === 'swipe_down_confront') return 'bottom';
  if (event === 'swipe_left_play_cool') return 'left';
  if (event === 'swipe_right_fix') return 'right';
  if (event === 'long_press_suppress') return 'hold';
  if (event === 'double_tap_meme') return 'double';
  if (event === 'shake_spiral') return 'shake';
  if (event === 'timed_hesitation') return 'pause';
  if (event === 'fast_fling_nope') return 'fling';
  return 'center';
}

function motionConstructor() {
  return window.DeviceMotionEvent as DeviceMotionConstructorWithPermission | undefined;
}

function canRequestMotionPermission() {
  return typeof motionConstructor()?.requestPermission === 'function';
}

export function GestureSurface({
  locale = 'en',
  onSelect,
  supportedEvents = fallbackEvents,
  disabled = false,
  activeEvent = null
}: {
  locale?: Locale;
  onSelect: (event: CoreEvent, telemetry: GestureTelemetry) => void;
  supportedEvents?: CoreEvent[];
  disabled?: boolean;
  activeEvent?: CoreEvent | null;
}) {
  const events = supportedEvents.length ? supportedEvents : fallbackEvents;
  const eventSet = useMemo(() => new Set(events), [events]);
  const pointer = useRef<PointerSnapshot | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const feedbackTimer = useRef<number | null>(null);
  const triggeredByHold = useRef(false);
  const lastShakeAt = useRef(Number.NEGATIVE_INFINITY);
  const motionPermissionRequested = useRef(false);
  const [feedbackEvent, setFeedbackEvent] = useState<CoreEvent | null>(null);
  const [motionPermissionGranted, setMotionPermissionGranted] = useState(false);
  const copy = stageCopy[locale];

  function clearLongPressTimer() {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function clearFeedbackTimer() {
    if (feedbackTimer.current !== null) {
      window.clearTimeout(feedbackTimer.current);
      feedbackTimer.current = null;
    }
  }

  function trigger(event: CoreEvent, telemetry: GestureTelemetry) {
    if (disabled || !eventSet.has(event)) return;
    setFeedbackEvent(event);
    clearFeedbackTimer();
    feedbackTimer.current = window.setTimeout(() => setFeedbackEvent(null), feedbackVisibleMs);
    onSelect(event, telemetry);
  }

  function triggerShake(source: 'motion' | 'fallback') {
    const now = Date.now();
    if (source === 'motion' && now - lastShakeAt.current < 850) return;
    lastShakeAt.current = now;
    trigger('shake_spiral', { distance: 0, dwellTimeMs: 0, velocity: 0, reversalCount: 0 });
  }

  async function requestMotionPermission() {
    const deviceMotion = motionConstructor();
    if (!deviceMotion?.requestPermission || motionPermissionRequested.current) return;

    motionPermissionRequested.current = true;
    try {
      const result = await deviceMotion.requestPermission();
      setMotionPermissionGranted(result === 'granted');
    } catch {
      setMotionPermissionGranted(false);
    }
  }

  function telemetryFor(endX: number, endY: number): GestureTelemetry {
    const snapshot = pointer.current;
    const now = performance.now();
    if (!snapshot) {
      return { distance: 0, dwellTimeMs: 0, velocity: 0, reversalCount: 0 };
    }

    const distance = distanceBetween(snapshot.startX, snapshot.startY, endX, endY);
    const dwellTimeMs = Math.max(1, now - snapshot.startTime);
    return {
      distance,
      dwellTimeMs,
      velocity: distance / dwellTimeMs,
      reversalCount: snapshot.reversalCount
    };
  }

  function beginPress(pointerId: number, point: { x: number; y: number }) {
    if (disabled) return;
    triggeredByHold.current = false;
    pointer.current = {
      pointerId,
      startX: point.x,
      startY: point.y,
      reversalCount: 0,
      startTime: performance.now()
    };

    clearLongPressTimer();
    if (eventSet.has('long_press_suppress')) {
      longPressTimer.current = window.setTimeout(() => {
        triggeredByHold.current = true;
        trigger('long_press_suppress', telemetryFor(point.x, point.y));
      }, 680);
    }
  }

  function movePress(pointerId: number, point: { x: number; y: number }) {
    const snapshot = pointer.current;
    if (!snapshot || snapshot.pointerId !== pointerId) return;

    const nextDirection = dominantDirection(point.x - snapshot.startX, point.y - snapshot.startY);
    if (!nextDirection) return;
    if (snapshot.lastDirection && snapshot.lastDirection !== nextDirection) {
      snapshot.reversalCount += 1;
    }
    snapshot.lastDirection = nextDirection;
  }

  function endPress(pointerId: number, point: { x: number; y: number }) {
    const snapshot = pointer.current;
    clearLongPressTimer();
    if (!snapshot || snapshot.pointerId !== pointerId) return;
    if (triggeredByHold.current) {
      pointer.current = null;
      return;
    }

    const dx = point.x - snapshot.startX;
    const dy = point.y - snapshot.startY;
    const telemetry = telemetryFor(point.x, point.y);
    pointer.current = null;
    if (telemetry.distance < 56) return;

    const direction = dominantDirection(dx, dy);
    if (!direction) return;
    const directionalEvent = directionEvents[direction];
    const selectedEvent =
      eventSet.has('fast_fling_nope') && telemetry.distance >= 120 && telemetry.velocity > 0.65
        ? 'fast_fling_nope'
        : directionalEvent;
    trigger(selectedEvent, telemetry);
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    const point = pointFromPointerEvent(event);
    beginPress(event.pointerId, point);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    movePress(event.pointerId, pointFromPointerEvent(event));
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    endPress(event.pointerId, pointFromPointerEvent(event));
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement>) {
    beginPress(-1, pointFromMouseEvent(event));
  }

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    movePress(-1, pointFromMouseEvent(event));
  }

  function handleMouseUp(event: React.MouseEvent<HTMLDivElement>) {
    endPress(-1, pointFromMouseEvent(event));
  }

  function handlePointerCancel() {
    clearLongPressTimer();
    pointer.current = null;
  }

  function handleDoubleClick() {
    trigger('double_tap_meme', { distance: 0, dwellTimeMs: 0, velocity: 0, reversalCount: 0 });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const keyboardTelemetry = { distance: 0, dwellTimeMs: 0, velocity: 0, reversalCount: 0 };
    if (event.key === 'ArrowUp') trigger('swipe_up_escape', keyboardTelemetry);
    if (event.key === 'ArrowDown') trigger('swipe_down_confront', keyboardTelemetry);
    if (event.key === 'ArrowLeft') trigger('swipe_left_play_cool', keyboardTelemetry);
    if (event.key === 'ArrowRight') trigger('swipe_right_fix', keyboardTelemetry);
    if (event.key === 'Enter' || event.key === ' ') trigger('double_tap_meme', keyboardTelemetry);
    if (event.key.toLowerCase() === 's' && eventSet.has('shake_spiral')) {
      event.preventDefault();
      void requestMotionPermission();
      triggerShake('fallback');
    }
  }

  function handleShakeFallback(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    void requestMotionPermission();
    triggerShake('fallback');
  }

  useEffect(() => {
    if (disabled || !eventSet.has('timed_hesitation')) return undefined;
    const timer = window.setTimeout(() => {
      trigger('timed_hesitation', { distance: 0, dwellTimeMs: hesitationDelayMs, velocity: 0, reversalCount: 0 });
    }, hesitationDelayMs);
    return () => window.clearTimeout(timer);
  }, [disabled, eventSet]);

  useEffect(() => {
    if (disabled || !eventSet.has('shake_spiral')) return undefined;
    if (canRequestMotionPermission() && !motionPermissionGranted) return undefined;

    function handleDeviceMotion(event: DeviceMotionEvent) {
      const acceleration = event.accelerationIncludingGravity;
      const force =
        Math.abs(acceleration?.x ?? 0) + Math.abs(acceleration?.y ?? 0) + Math.abs(acceleration?.z ?? 0);
      if (force > 34) {
        triggerShake('motion');
      }
    }

    window.addEventListener('devicemotion', handleDeviceMotion);
    return () => window.removeEventListener('devicemotion', handleDeviceMotion);
  }, [disabled, eventSet, motionPermissionGranted]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      clearFeedbackTimer();
    };
  }, []);

  const displayEvent = activeEvent ?? feedbackEvent;
  const feedbackCopy = !activeEvent && feedbackEvent ? hintCopy[locale][feedbackEvent] : null;
  const locked = disabled && Boolean(activeEvent);

  return (
    <div className="gesture-surface">
      <div
        aria-disabled={disabled}
        aria-label={copy.label}
        className="gesture-stage"
        data-feedback={displayEvent ? hintPosition(displayEvent) : undefined}
        data-locked={locked ? 'true' : undefined}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onPointerCancel={handlePointerCancel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        role="application"
        tabIndex={disabled ? -1 : 0}
      >
        <p className="gesture-instruction">{copy.instruction}</p>
        <div className="gesture-direction-ring">
          {events.filter((event) => directionalCoreEvents.includes(event)).map((event) => (
            <span
              className={`gesture-hint ${hintPosition(event)}`}
              data-active={displayEvent === event ? 'true' : undefined}
              key={event}
            >
              <strong>{hintCopy[locale][event].gesture}</strong>
              <small>{hintCopy[locale][event].label}</small>
            </span>
          ))}
          <div className="gesture-dial" aria-hidden="true">
            <span>{copy.dial}</span>
          </div>
        </div>
        <div className="gesture-extra-row">
          {events.filter((event) => extraCoreEvents.includes(event)).map((event) => (
            event === 'shake_spiral' ? (
              <button
                className={`gesture-hint ${hintPosition(event)}`}
                data-active={displayEvent === event ? 'true' : undefined}
                disabled={disabled}
                key={event}
                onClick={handleShakeFallback}
                onMouseDown={(mouseEvent) => mouseEvent.stopPropagation()}
                onPointerDown={(pointerEvent) => pointerEvent.stopPropagation()}
                type="button"
              >
                <strong>{hintCopy[locale][event].gesture}</strong>
                <small>{hintCopy[locale][event].label}</small>
              </button>
            ) : (
              <span
                className={`gesture-hint ${hintPosition(event)}`}
                data-active={displayEvent === event ? 'true' : undefined}
                key={event}
              >
                <strong>{hintCopy[locale][event].gesture}</strong>
                <small>{hintCopy[locale][event].label}</small>
              </span>
            )
          ))}
        </div>
        {locked ? <div className="gesture-lock-mask" aria-hidden="true" /> : null}
        <output className="gesture-feedback" aria-live="polite">
          {feedbackCopy ? `${copy.feedbackPrefix}: ${feedbackCopy.gesture} ${feedbackCopy.label}` : ''}
        </output>
      </div>
    </div>
  );
}
